import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import * as actions from './redux/actions';
import CollectionHeaderNav from './CollectionHeaderNav';
import types from '../../common/formElements/CollectionSectionType';
import CustomLoader from '../common/CustomLoader';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import CommonPageComponent from '../../common/ui/CommonPageComponent';
import ParentCard from '../common/ParentCard';
import { pluginCode } from 'drapcode-constant';
import CustomSelect from '../home/collections/CustomSelect';
import CustomRadio from '../../common/ui/CustomRadio';
import { showAlertHtml } from '../../common/utils/alert';
import { toast } from 'react-toastify';

export class TypesenseMapping extends Component {
  static propTypes = {
    collections: PropTypes.object.isRequired,
    actions: PropTypes.object.isRequired,
  };

  componentDidMount() {
    const { match, actions } = this.props;
    const { params } = match;
    const { projectId, collectionId, version } = params;
    actions.loadProjectCollections({ projectId, version });
    actions.loadProjectCollectionDetail({ projectId, collectionId, version });
    actions.loadProjectPlugins({ projectId, version });
  }

  handleSubmit = (values) => {
    const { collections, match, actions } = this.props;
    const { selectedCollection } = collections;
    const { projectId, version } = match.params;
    const transformedValues = Object.keys(values)
      .filter((key) => !key.endsWith('_priority'))
      .map((key) => {
        const schemaValue = values[key];
        const priorityValue = values[`${key}_priority`] ? values[`${key}_priority`].value : 1;
        if (!schemaValue) return null;
        const field = selectedCollection.fields.find((field) => field.fieldName === key);
        const fieldType = field ? field.type : '';
        const fieldTitle = field ? field.fieldTitle.en : '';
        return {
          fieldName: key,
          schema: schemaValue,
          priority: priorityValue,
          fieldType,
          fieldTitle,
        };
      })
      .filter(Boolean);
    if (transformedValues.length === 0) {
      console.warn('No valid fields to submit.');
      toast.error('Please add at least one field to the schema before saving.');
      return;
    }
    const data = { typesenseMapping: transformedValues };
    const preConfirm = () => {
      actions.createTypesenseMappingInCollection({
        projectId,
        version,
        data,
        selectedCollection,
      });
    };
    showAlertHtml(
      selectedCollection.typesenseMapping.length
        ? `Are you sure you want to update the Typesense mapping?<br/><br/>
       <span>Updating the mapping will require reindexing the data to reflect the changes.</span>`
        : `Save the Typesense mapping?<br/><br/>
       <span>To include existing data in Typesense, use the "Reindex Data for Typesense Collection" event after saving.</span>`,
      preConfirm,
      selectedCollection.typesenseMapping.length
        ? 'Yes, update the mapping'
        : 'Yes, save the mapping',
      selectedCollection.typesenseMapping.length ? 'warning' : 'info',
    );
  };

  getInitialValues = (selectedCollection) => {
    return selectedCollection.fields.reduce((acc, field, index) => {
      const fieldName = field.fieldName;
      const typesenseMapping = selectedCollection.typesenseMapping.find(
        (item) => item.fieldName === fieldName,
      );
      acc[fieldName] = typesenseMapping ? typesenseMapping.schema || '' : '';
      acc[`${fieldName}_priority`] = typesenseMapping
        ? { label: typesenseMapping.priority.toString(), value: typesenseMapping.priority }
        : '';
      return acc;
    }, {});
  };

  render() {
    const { collections, match } = this.props;
    const { selectedCollection } = collections;
    const { projectId, version } = match.params;
    const { loadProjectCollectionsPending, loadProjectCollectionDetailPending, plugins } =
      collections;
    const hasTypesensePlugin = plugins.some(
      (plugin) => plugin.code === pluginCode.TYPESENSE_SEARCH,
    );
    if (loadProjectCollectionsPending || loadProjectCollectionDetailPending) {
      return (
        <CommonPageComponent>
          <CollectionHeaderNav
            label={types.TYPESENSE_MAPPING}
            projectId={projectId}
            version={version}
            selectedCollection={selectedCollection}
            loadProjectCollectionsPending={loadProjectCollectionsPending}
            loadProjectCollectionDetailPending={loadProjectCollectionDetailPending}
          />
          <div className="text-center">
            <CustomLoader
              loading={loadProjectCollectionsPending || loadProjectCollectionDetailPending}
              size="20px"
              color="#303453"
            />
          </div>
        </CommonPageComponent>
      );
    }

    if (!hasTypesensePlugin) {
      return (
        <CommonPageComponent>
          <CollectionHeaderNav
            label={types.TYPESENSE_MAPPING}
            projectId={projectId}
            version={version}
            selectedCollection={selectedCollection}
            loadProjectCollectionsPending={loadProjectCollectionsPending}
            loadProjectCollectionDetailPending={loadProjectCollectionDetailPending}
          />
          <ParentCard
            icon="typesenseMapping"
            header={`Typesense Mapping ${
              selectedCollection && ` For ${selectedCollection.collectionTitle}`
            }`}
            subtitle="Create Typesense Mapping for collection."
          >
            <div className="text-center">
              <h5>Please install the Typesense Search plugin to manage mapping.</h5>
            </div>
          </ParentCard>
        </CommonPageComponent>
      );
    }

    const initialValues = this.getInitialValues(selectedCollection);
    return (
      <CommonPageComponent>
        <CollectionHeaderNav
          label={types.TYPESENSE_MAPPING}
          projectId={projectId}
          version={version}
          selectedCollection={selectedCollection}
          loadProjectCollectionsPending={loadProjectCollectionsPending}
          loadProjectCollectionDetailPending={loadProjectCollectionDetailPending}
        />
        {selectedCollection && (
          <ParentCard
            icon="typesenseMapping"
            header={`Typesense Mapping ${
              selectedCollection && ` For ${selectedCollection.collectionTitle}`
            }`}
            subtitle="Create Typesense Mapping for a collection."
          >
            <Formik initialValues={initialValues} onSubmit={this.handleSubmit}>
              {({ setFieldValue, setFieldTouched, values }) => {
                const fieldCount = selectedCollection.fields.length;
                const priorityOptions = Array.from({ length: fieldCount }, (_, i) => i + 1);
                return (
                  <Form className="form-horizontal">
                    <div className="row">
                      <div className="col-3">
                        <h5>Collection Fields</h5>
                      </div>
                      <div className="col-6">
                        <h5>Typesense Schema</h5>
                      </div>
                      <div className="col-3">
                        <h5>Priority</h5>
                      </div>
                    </div>
                    {selectedCollection.fields.map((field) => (
                      <div className="row mx-auto mb-3" key={field.fieldName}>
                        <div className="col-md-3 col-lg-3 col-sm-12 col-xs-12">
                          <span className="input-group-text">{field.fieldTitle.en}</span>
                        </div>
                        <div className="col-md-6 col-lg-6 col-sm-12 col-xs-12">
                          <div className="form-check">
                            <CustomRadio label="Add in Schema" isRadio={false}>
                              <Field
                                type="checkbox"
                                name={field.fieldName}
                                className="form-check-input"
                              />
                            </CustomRadio>
                            <small>( Add in Typesense Schema.)</small>
                            <p className="errors">
                              <ErrorMessage name={field.fieldName} />
                            </p>
                          </div>
                        </div>
                        <div className="col-md-3 col-lg-3 col-sm-12 col-xs-12">
                          <CustomSelect
                            name={`${field.fieldName}_priority`}
                            isMulti={false}
                            value={values[`${field.fieldName}_priority`]}
                            options={priorityOptions.map((num) => ({
                              label: num,
                              value: num,
                            }))}
                            onChange={setFieldValue}
                            onBlur={setFieldTouched}
                            placeholder="Select Priority"
                          />
                        </div>
                      </div>
                    ))}
                    <div className="text-right mt-3">
                      <button type="submit" className="btn btn-theme-submit btn-md">
                        {selectedCollection.typesenseMapping &&
                        selectedCollection.typesenseMapping.length ? (
                          <>
                            <i className="wb webconnect-save-regular"></i>&nbsp;Update&nbsp;
                          </>
                        ) : (
                          <>
                            <i className="wb webconnect-plus"></i>&nbsp;Save&nbsp;
                          </>
                        )}
                        <CustomLoader loading={false} />
                      </button>
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </ParentCard>
        )}
      </CommonPageComponent>
    );
  }
}

function mapStateToProps(state) {
  return {
    collections: state.collections,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ ...actions }, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(TypesenseMapping);
